import axios from "axios";
import { useUserStore } from "@/stores/user";
import router from "@/pc/router";
import { ElMessage } from "element-plus";
import { USER_INFO, USER_TOKEN } from "@/config/catch";
import { useLocalStorage } from "@/utils/storage";

const http = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 10000,
});

const AUTH_REQUIRED_PREFIXES = [
  "/mall/cart/",
  "/mall/order/",
  "/mall/address/",
  "/mall/invoice/",
  "/mall/user/",
  "/pay/",
];

let redirectingToLogin = false;

const normalizeRequestPath = (url = "") => {
  const rawUrl = String(url || "");
  if (!rawUrl) return "";
  try {
    return new URL(rawUrl, window.location.origin).pathname;
  } catch (error) {
    const path = rawUrl.split("?")[0];
    return path.startsWith("/") ? path : `/${path}`;
  }
};

const isAuthRequiredRequest = (url) => {
  const path = normalizeRequestPath(url);
  return AUTH_REQUIRED_PREFIXES.some((prefix) => path.startsWith(prefix));
};

const getAuthToken = (userStore) => {
  return (
    userStore.token ||
    userStore.userInfo?.token ||
    useLocalStorage.get(USER_TOKEN) ||
    useLocalStorage.get(USER_INFO)?.token ||
    ""
  );
};

const clearAuthState = (userStore = useUserStore()) => {
  if (typeof userStore.clearUserState === "function") {
    userStore.clearUserState();
  } else {
    userStore.userInfo = null;
    userStore.token = null;
    useLocalStorage.set(USER_INFO, null);
    useLocalStorage.set(USER_TOKEN, null);
    localStorage.removeItem("user");
  }
  localStorage.removeItem("cart");
};

const createAuthError = (message = "请先登录") => {
  const error = new Error(message);
  error.isAuthError = true;
  error.status = 401;
  return error;
};

const redirectToLogin = (message = "请先登录") => {
  if (typeof window === "undefined" || redirectingToLogin) return;

  const currentRoute = router.currentRoute?.value;
  if (currentRoute?.path === "/login") return;

  const redirect = currentRoute?.fullPath || window.location.hash.replace(/^#/, "") || "/";
  redirectingToLogin = true;
  ElMessage.warning(message);
  router
    .push({ path: "/login", query: { redirect, loginMode: "page" } })
    .catch(() => undefined)
    .finally(() => {
      setTimeout(() => {
        redirectingToLogin = false;
      }, 500);
    });
};

// 请求拦截
http.interceptors.request.use((config) => {
  const userStore = useUserStore();
  const token = getAuthToken(userStore);
  if (token) {
    config.headers = config.headers || {};
    config.headers.token = token;
    if (userStore.token !== token) userStore.setUserToken(token);
  } else if (isAuthRequiredRequest(config.url)) {
    clearAuthState(userStore);
    redirectToLogin("请先登录");
    return Promise.reject(createAuthError("请先登录"));
  }
  return config;
});

// 响应拦截
http.interceptors.response.use(
  (response) => {
    // 未设置状态码则默认成功状态
    const code = response.data.code || 200;
    const msg = response.data.message || response.data.msg || "操作失败";
    // return false
    if (code === 401) {
      // 防止重复处理401（避免logout接口本身401导致死循环）
      if (!window.__handling401) {
        window.__handling401 = true;
        clearAuthState();
        redirectToLogin(msg || "用户未登录");

        setTimeout(() => {
          window.__handling401 = false;
        }, 3000);
      }
      return Promise.reject(createAuthError(msg || "登录已失效"));
    } else if (code === 500) {
      ElMessage({ message: msg, type: "error" });
      return Promise.reject(new Error(msg));
    } else {
      return response.data;
    }
  },
  (error) => {
    if (error?.response?.status === 401) {
      const msg = error.response.data?.message || "用户未登录";
      clearAuthState();
      redirectToLogin(msg);
      return Promise.reject(createAuthError(msg));
    }
    return Promise.reject(error);
  }
);

export default http;
